import React from 'react'
import { InferGetStaticPropsType } from 'next'
import { useBreakpointValue } from '@hub/design-system-base'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { getDefaultNationalMetaData } from '@scentregroup/shared/helpers/get-default-national-metadata'
import { REVALIDATION_PERIODS } from '@scentregroup/shared/constants'
import { useGetHrefContext } from '@scentregroup/shared/context'
import Layout from '@scentregroup/shared/hub-components/layout'
import { fetchNationalStaticData } from '@scentregroup/shared/hub-components/universal-header'
import { GetStaticProps, GetStaticPropsContext } from '../types/next'
import { LDJson } from '../lib/ldjson'
import { makeApolloClient } from '../lib/apolloClient'
import { getNationalHomepage } from '../lib/page-templates/fetching'
import { Template } from '../components/page-templates/template'

export const getStaticProps: GetStaticProps = async ({
  locale,
}: GetStaticPropsContext) => {
  const client = makeApolloClient()
  const country = localeToCountry(locale)

  const [nationalHeaderData, template] = await Promise.all([
    fetchNationalStaticData(client, { country }),
    getNationalHomepage(client, country),
  ])

  if (!nationalHeaderData || !template) {
    return {
      notFound: true,
      revalidate: REVALIDATION_PERIODS.notFound,
    }
  }

  return {
    props: {
      country,
      nationalHeaderData,
      template,
    },
    revalidate: REVALIDATION_PERIODS.static,
  }
}

const IndexPage = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const headerHasBottomMargin = useBreakpointValue({ base: false, sm: true })
  const { country, template, nationalHeaderData } = props
  const { title, shortDescription } = template
  const canonical = useGetHrefContext()()
  const schema = ({ url }: { url: string }) =>
    url
      ? {
          '@context': 'http://schema.org',
          '@type': 'WebSite',
          url,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${url}search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        }
      : null
  const defaultMetaData = getDefaultNationalMetaData({
    country,
    type: 'homepage',
  })

  const metaData = {
    title: title || defaultMetaData.title,
    description: shortDescription || defaultMetaData.description,
    canonical,
  }

  return (
    <>
      <LDJson data={schema({ url: canonical })} />
      <Layout
        country={country}
        centre={undefined}
        header={{
          variant: 'national',
          data: nationalHeaderData,
          hasBottomMargin: headerHasBottomMargin,
        }}
        metaData={metaData}
      >
        <Template template={template} />
      </Layout>
    </>
  )
}

export default IndexPage
